import { request } from "@/utilities/libs/request.ts";
import {
  IRequestArgs,
  IResponseData,
  ListResponseData,
} from "@/utilities/types/requests.ts";
import { ERequestMethods } from "@/utilities/enums/RequestMethod.ts";
import {
  ICreatePushNotificationDTO,
  IPushNotification,
  ISchedulePushNotificationDTO,
} from "@/utilities/types/PushNotification";
import { E_PUSH_NOTIFICATION } from "@/utilities/enums/PushNotification";
import { transformFilterQueriesToObject } from "@/utilities/functions/query";

// GET /push-blast — list with optional status, fromDate, toDate, page, limit
function list({ queryParams }: IRequestArgs) {
  const filterQueries = queryParams?.query?.filter;
  const transformQueries = transformFilterQueriesToObject(filterQueries || []);
  const query = {
    sort: queryParams?.query?.sort,
    ...queryParams?.query?.search,
    ...queryParams?.pagination,
    ...transformQueries,
  };
  return request<ListResponseData<IPushNotification>>({
    method: ERequestMethods.GET,
    url: E_PUSH_NOTIFICATION.LISTING,
    params: query,
  });
}

// GET /push-blast/:id — detail with delivery stats
function get(id: string) {
  return request<IResponseData<IPushNotification>>({
    method: ERequestMethods.GET,
    url: E_PUSH_NOTIFICATION.MAIN(id),
  });
}

// POST /push-blast/drafts — create a new draft (status: DRAFT)
function create(data: ICreatePushNotificationDTO) {
  return request<IResponseData<IPushNotification>>({
    method: ERequestMethods.POST,
    url: E_PUSH_NOTIFICATION.LISTING_DRAFTS,
    data,
  });
}

// PATCH /push-blast/drafts/:id — update draft content (only DRAFT/SCHEDULED)
function updateDraft(
  id: string,
  data: Partial<ICreatePushNotificationDTO>,
) {
  return request<IResponseData<IPushNotification>>({
    method: ERequestMethods.PATCH,
    url: E_PUSH_NOTIFICATION.MAIN_DRAFT(id),
    data,
  });
}

// POST /push-blast/:id/cancel — cancel a DRAFT or SCHEDULED blast
function cancel(id: string) {
  return request<IResponseData<IPushNotification>>({
    method: ERequestMethods.POST,
    url: E_PUSH_NOTIFICATION.CANCEL(id),
  });
}

// POST /push-blast/:id/schedule — schedule a DRAFT for later
function schedule(id: string, data: ISchedulePushNotificationDTO) {
  return request<IResponseData<IPushNotification>>({
    method: ERequestMethods.POST,
    url: E_PUSH_NOTIFICATION.SCHEDULE(id),
    data,
  });
}

// POST /push-blast/:id/send — send immediately (DRAFT or SCHEDULED)
// Returns SENT transition: poll detail afterwards to detect SENT/FAILED.
function send(id: string) {
  return request<IResponseData<IPushNotification>>({
    method: ERequestMethods.POST,
    url: E_PUSH_NOTIFICATION.SEND(id),
  });
}

const PushNotificationRepository = Object.freeze({
  list,
  get,
  create,
  updateDraft,
  cancel,
  schedule,
  send,
});

export default PushNotificationRepository;
